import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "@shared/ui/tooltip";
import { HelpCircle } from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";

export const WorkOrderMaintenance: React.FC<{ data: any }> = ({ data }) => {
  const navigate = useNavigate();

  const getBadgeVariant = (priority: string) => {
    switch (priority) {
      case "High":
        return "destructive";
      case "Medium":
        return "secondary";
      case "Low":
        return "default";
      default:
        return "secondary";
    }
  };

  const handleViewDetails = (row: any) => {
    const status = row.statusDisplay?.toLowerCase();

    // Determine the route based on status
    let routePrefix = "";

    switch (status) {
      case "created":
      case "overdue":
        routePrefix = "pending";
        break;
      case "assigned":
      case "accepted":
      case "in-progress":
      case "in progress":
        routePrefix = "assigned";
        break;
      case "completed":
      case "rejected":
        routePrefix = "completed";
        break;
      case "canceled":
      case "cancelled":
      case "approved":
        routePrefix = "history";
        break;
      default:
        // Default to pending for unknown statuses
        routePrefix = "pending";
    }

    navigate(`/ax/service-order/landing/${routePrefix}/view/system/${row.id}`);
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <div className="flex item-center gap-2">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                Active Work Orders
              </CardTitle>
            </div>
            <div className="mt-1">
              <Tooltip>
                <TooltipTrigger>
                  <HelpCircle className="h-4 w-4 text-gray-400" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs">
                    A list of the most important maintenance tasks currently in
                    progress or scheduled for this system.
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {data?.activeWorkOrders && data.activeWorkOrders.length > 0 ? (
            data.activeWorkOrders.map((wo) => (
              <div
                key={wo.id}
                className="flex items-center justify-between p-3 rounded mt-3"
              >
                <div>
                  <p className="font-medium">{wo.sopName || "NA"}</p>
                  <p className="text-sm text-gray-600">
                    {wo.jobCardNo || "N/A"} • Assigned to:{" "}
                    {wo.fieldForceName || "Unassigned"}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={getBadgeVariant(wo.statusDisplay)}>
                    {wo.statusDisplay || "N/A"}
                  </Badge>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleViewDetails(wo)}
                  >
                    View
                  </Button>
                </div>
              </div>
            ))
          ) : (
            <div className="p-3 text-center text-gray-500 mt-3">
              No work orders found.
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex item-center gap-2">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                Frequent Failure Assets
              </CardTitle>
            </div>
            <div className="mt-1">
              <Tooltip>
                <TooltipTrigger>
                  <HelpCircle className="h-4 w-4 text-gray-400" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs">
                    The worst-acting' assets in this system, ranked by the
                    number of failures. These may be candidates for overhaul or
                    replacement.
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {data?.frequentFailureAssets &&
          data.frequentFailureAssets.length > 0 ? (
            data.frequentFailureAssets.map((asset) => (
              <div
                key={asset.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded mt-3"
              >
                <div>
                  <p className="font-medium">{asset.requestUsername ?? "NA"}</p>
                  <p className="text-sm text-gray-600">
                    {asset.requestAccountNo ?? "NA"}
                  </p>
                  <p className="text-xs text-gray-500">
                    Last: {asset.latestCreatedDate ?? "NA"}
                  </p>
                </div>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      navigate(
                        `/ax/asset-registry/assets/view/${asset.requestId}`
                      )
                    }
                  >
                    View
                  </Button>
                </div>
              </div>
            ))
          ) : (
            <div className="p-3 text-center text-gray-500 mt-3">
              No frequent failure assets found.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
